import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled from 'styled-components';
import Calendar from 'react-calendar';
import moment from 'moment';
import { PostServiceCaller } from '../services/serviceCaller';
import { API_URL } from '../constants';
import { FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';

const AttendanceCalendar = ({ attendanceCalenderOpen, setAttendanceCalenderOpen }) => {
  const [selectedDates, setSelectedDates] = useState([]);
  const [isCalCulated, setIsCalCulated] = useState(0);
  const [value, setValue] = useState(new Date());
  const [calendarData, setCalendarData] = useState({
    holidays: [],
    approved: [],
    present: [],
    leaves: [],
    inactive: [],
    disableDays: []
  });
  const [EMPDOJ, setEMPDOJ] = useState(null);
  const [monthID, setMonthID] = useState(null);
  const [userShiftID, setUserShiftID] = useState(null);
  const [canMarkAttendanceSpecial, setCanMarkAttendanceSpecial] = useState(false);
  
  // Add loading state
  const [isLoading, setIsLoading] = useState(true);
  const [isCalendarDisabled, setIsCalendarDisabled] = useState(true);

  // Track if this is the initial load
  const isInitialMount = useRef(true);
  // Track if we should skip the next API call
  const shouldSkipNextAPICall = useRef(false);

  const calendarSets = useMemo(() => {
    const toSet = (arr) => new Set(arr || []);
    return {
      holidays: toSet(calendarData.holidays),
      approved: toSet(calendarData.approved),
      present: toSet(calendarData.present),
      leaves: toSet(calendarData.leaves),
      inactive: toSet(calendarData.inactive),
    };
  }, [calendarData]);

  const handleDayClick = (date) => {
    // Prevent clicking if calendar is disabled
    if (isCalendarDisabled) return;
    
    // Prevent clicking on previous month dates shown in current month view
    const clickedMonth = date.getMonth();
    const activeMonth = activeStartDate.getMonth();
    const activeYear = activeStartDate.getFullYear();
    const clickedYear = date.getFullYear();

    // If the clicked date is outside the visible month, ignore it
    if (clickedMonth !== activeMonth || clickedYear !== activeYear) {
      return;
    }

    onActiveDateChange(date);
  };

  const [activeStartDate, setActiveStartDate] = useState(new Date());
  
  // Fetch data only when month changes, not on date click
  useEffect(() => {
    // Skip API call on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchCalendarData();
      return;
    }

    // Skip API call if flag is set (prevents call on date selection)
    if (shouldSkipNextAPICall.current) {
      shouldSkipNextAPICall.current = false;
      return;
    }

    fetchCalendarData();
  }, [activeStartDate]); // This will only trigger when month changes

  const fetchCalendarData = () => {
    setIsLoading(true);
    setIsCalendarDisabled(true);
    
    const formattedDate = moment(activeStartDate).format("YYYY-MM-DD");

    PostServiceCaller(
      `${API_URL}fetch-days`,
      { current_date: formattedDate },
      "post"
    )
      .then((response) => {
        const data = response?.data?.[0]?.data || {};
        const employeeDOJ = response?.data?.[0]?.employeeDOJ;
        setIsCalCulated(response?.data?.[0]?.payrollCalculated);
        setEMPDOJ(employeeDOJ);
        setMonthID(response?.data.currentmonthid);
        setUserShiftID(response?.data?.[0]?.shiftID);
        setCanMarkAttendanceSpecial(response?.data?.[0]?.canMarkAttendanceSpecial || false);

        const formatDates = (dates) =>
          Object.values(dates || {}).map(date => moment(date).format("YYYY-MM-DD"));

        setCalendarData({
          holidays: formatDates(data.holidays),
          leaves: formatDates(data.Leaves),
          approved: formatDates(data.approve),
          present: formatDates(data.present),
          inactive: formatDates(data.inactive),
          disableDays: data.disableDays || []
        });
      })
      .catch((error) => {
        console.error("Error fetching calendar data:", error);
        toast.error("Failed to load calendar data");
      })
      .finally(() => {
        setIsLoading(false);
        setIsCalendarDisabled(false); // Enable calendar after API call completes
      });
  };
  
  const tileClassName = useCallback(({ date, view }) => {
    if (view !== "month") return null;
    const dateStr = moment(date).format("YYYY-MM-DD");

    const classes = [];
    if (calendarSets.holidays.has(dateStr)) classes.push("holiday");
    if (calendarSets.inactive.has(dateStr)) classes.push("inactive");
    if (calendarSets.approved.has(dateStr)) classes.push("approved");
    if (calendarSets.leaves.has(dateStr)) classes.push("leave");
    if (calendarSets.present.has(dateStr)) classes.push("present");
    if (selectedDates.includes(dateStr)) classes.push("selected-date");
    
    // Add loading class if calendar is disabled
    if (isCalendarDisabled) classes.push("disabled-tile");

    return classes.join(" ");
  }, [calendarSets, selectedDates, isCalendarDisabled]);


  const [todayDateCheck, setTodayDateCheck] = useState(true); // today selected by default

  const onActiveDateChange = (date) => {
    // Prevent selection if calendar is disabled
    if (isCalendarDisabled) return;
    
    const dateStr = moment(date).format("YYYY-MM-DD");

    // Prevent selection of restricted dates
    if (
      calendarData.inactive.includes(dateStr) ||
      calendarData.holidays.includes(dateStr) ||
      calendarData.present.includes(dateStr) ||
      calendarData.approved.includes(dateStr)
    ) {
      return;
    }

    setSelectedDates((prevDates) => {
      if (prevDates.includes(dateStr)) {
        return prevDates.filter(d => d !== dateStr); // unselect
      } else {
        return [...prevDates, dateStr]; // select
      }
    });

    setValue(date);
    
    // Set flag to skip API call when date changes
    shouldSkipNextAPICall.current = true;
  };

  const handleActiveStartDateChange = ({ activeStartDate: newActiveStartDate }) => {
    // Prevent navigation if calendar is disabled
    if (isCalendarDisabled) return;
    
    setActiveStartDate(newActiveStartDate);
    setSelectedDates([]);
    // The useEffect will detect month change and call fetchCalendarData
  };
  
  const [currentDay] = useState(new Date().getDate());
  
  const tileDisabled = useCallback(
    ({ date, view }) => {
      // Disable all tiles if calendar is in loading/disabled state
      if (isCalendarDisabled) return true;
      
      if (view !== "month") return false;

      const dateStr = moment(date).format("YYYY-MM-DD");

      const currentDate = moment();
      const currentMonth = currentDate.month();
      const currentYear = currentDate.year();

      const prevMonth = moment().subtract(1, "month");
      const prevMonthNo = prevMonth.month();
      const prevMonthYear = prevMonth.year();

      // ✅ CASE 1: If isCalCulated === 0 → allow current + previous month
      if (isCalCulated === 0) {
        const isPrevMonth =
          date.getMonth() === prevMonthNo && date.getFullYear() === prevMonthYear;
        const isCurrMonth =
          date.getMonth() === currentMonth && date.getFullYear() === currentYear;

        // disable all months except prev & current
        if (!isPrevMonth && !isCurrMonth) {
          return true;
        }

        // disable invalid (inactive/present/approved)
        return (
          calendarSets.inactive.has(dateStr) ||
          calendarSets.present.has(dateStr) ||
          calendarSets.approved.has(dateStr)
        );
      }

      // ✅ CASE 2: Default logic
      // Disable all previous months
      if (
        (date.getMonth() < currentMonth && date.getFullYear() === currentYear) ||
        date.getFullYear() < currentYear
      ) {
        return true;
      }

      // Disable all next months
      if (
        (date.getMonth() > currentMonth && date.getFullYear() === currentYear) ||
        date.getFullYear() > currentYear
      ) {
        return true;
      }

      // Disable extra days from prev/next months in current view
      if (
        date.getMonth() !== activeStartDate.getMonth() ||
        date.getFullYear() !== activeStartDate.getFullYear()
      ) {
        return true;
      }

      // Disable inactive/present/approved in current month
      return (
        calendarSets.inactive.has(dateStr) ||
        calendarSets.present.has(dateStr) ||
        calendarSets.approved.has(dateStr)
      );
    },
    [isCalCulated, calendarSets, activeStartDate, isCalendarDisabled]
  );

  const handleSubmit = async () => {
    // Prevent submit if calendar is still loading
    if (isCalendarDisabled || isLoading) {
      toast.error("Calendar data is still loading. Please wait.");
      return;
    }
    
    // ✅ Step 1: Filter out invalid dates (inactive, holidays, present, approved, leaves)
    const validDates = selectedDates.filter(dateStr => {
      return (
        !calendarData.inactive.includes(dateStr) &&
        !calendarData.holidays.includes(dateStr) &&
        !calendarData.present.includes(dateStr) &&
        !calendarData.approved.includes(dateStr) &&
        !calendarData.leaves.includes(dateStr)
      );
    });

    if (validDates.length === 0) {
      toast.error("Choose dates to proceed.");
      return;
    }

    // ✅ Step 2: Find the latest selected date
    const maxSelected = moment.max(validDates.map(d => moment(d)));

    // ✅ Step 3: Build the required range of dates from 1st → latest selected
    const requiredDates = [];
    const startOfMonth = moment(maxSelected).startOf("month");
    let current = startOfMonth.clone();

    while (current.isSameOrBefore(maxSelected, "day")) {
      const dateStr = current.format("YYYY-MM-DD");
      
      if (
        new Date(dateStr) >= new Date(EMPDOJ) && // Added by Fazil in order to handle JoiningDate issue
        !calendarData.inactive.includes(dateStr) &&
        !calendarData.holidays.includes(dateStr) &&
        !calendarData.present.includes(dateStr) &&
        !calendarData.approved.includes(dateStr) &&
        !calendarData.leaves.includes(dateStr)
      ) {
        requiredDates.push(dateStr);
      }

      current.add(1, "day");
    }

    // ✅ Step 4: Check if all required dates are selected
    const missingDates = requiredDates.filter(d => !validDates.includes(d));
    
    if (
      missingDates.length > 0 &&
      maxSelected.isSame(moment(), "month") // 🔹 Only check for current month
    ) {
      // Added by Fazil
      toast.error(
        `You must select the following date(s): ${missingDates
          .map(d => moment(d).format("DD MMM"))
          .join(", ")}`
      );
      return;
    }

    // ✅ Step 5: Ensure today's date is selected if valid AND current month
    const today = moment().format("YYYY-MM-DD");
    const isTodayValid =
      !calendarData.inactive.includes(today) &&
      !calendarData.holidays.includes(today) &&
      !calendarData.present.includes(today) &&
      !calendarData.approved.includes(today) &&
      !calendarData.leaves.includes(today);

    if (
      maxSelected.isSame(moment(), "month") && // 🔹 Only check for current month
      isTodayValid &&
      !validDates.includes(today)
    ) {
      toast.error(`Please select today's date (${moment().format("DD MMM")})`);
      return;
    }

    // ✅ Step 6: Continue submit if everything is fine
    const formattedDates = validDates.map(date =>
      moment(date).format("YYYY-MM-DD")
    );

    const dateObject = {
      daystatus: 1,
      employeeID: "",
      rowstatus: 435,
      attendancestatus: 824,
      dates: formattedDates.join(","),
      MonthID: monthID,
    };

    try {
      setSubmit(true);
      const response = await PostServiceCaller(
        `${API_URL}mark-attendance`,
        dateObject,
        "post"
      );

      if (response?.status === 200) {
        toast.success("Attendance has been submitted successfully!");
        setSubmit(false);
        fetchCalendarData();
        setAttendanceCalenderOpen(false);
      }
    } catch (error) {
      setSubmit(false);
      console.error("Error occurred while posting data:", error);
      
      // Extract backend error message if available
      const errorMessage = error?.response?.data?.message || "Failed to mark attendance. Manual marking may be disabled for your shift.";
      toast.error(errorMessage);
    }
  };

  const isCurrentMonthView = () => {
    const currentDate = new Date();
    return (
      activeStartDate.getMonth() === currentDate.getMonth() &&
      activeStartDate.getFullYear() === currentDate.getFullYear()
    );
  };

  const StyledCalendar = styled(Calendar)`
  /* Calendar container */
  .react-calendar {
    width: max-content !important;
    max-width: 300px !important;
  }

  /* Navigation bar */
  .react-calendar__navigation {
    background: #fff;
    color: black;
  }

  .react-calendar__navigation button {
    background: #fff;
    color: black;
  }


  .react-calendar__navigation button[aria-label="Clear value"],
  .react-calendar__navigation button[aria-label="Go to today"] {
    display: none;
  }

  .react-calendar__navigation__label {
    font-size: 14px;
    font-weight: bold;
  }

  .react-calendar__navigation__label:hover {
    background: #007deb !important;
    color: white !important;
    border-radius: 10%;
  }

  .react-calendar__navigation__arrow {
    color: black;
    padding: 4px;
    font-size: 22px;
    font-weight: bold;
  }

  .react-calendar__navigation__arrow:hover,
  .react-calendar__navigation__arrow.react-calendar__navigation__prev-button:hover {
    background: #007deb !important;
    color: white !important;
    border-radius: 10%;
  }

  /* Tile hover effect */
  .react-calendar__tile:hover {
    background: linear-gradient(
      to bottom,
      #ffffff 0%,
      #e6f0ff 60%,
      #b3d1ff 100%
    ) !important;
    color: black !important;
    border-radius: 8px;
  }

  /* Day tile styles */
  .react-calendar__tile {
    font-size: 12px;
  }

  .react-calendar__tile.holiday {
    color: red !important;
    font-weight: bold;
    background-color: rgba(255, 0, 0, 0.1);
  }

  .react-calendar__tile.inactive {
    color: #9e9e9e !important;
    background-color: rgba(0, 0, 0, 0.05);
  }
  .react-calendar__tile.leave {
    color: #ed8d2d !important;
    background-color: rgba(0, 0, 0, 0.05);
  }

  .react-calendar__tile.present {
    color: #2e7d32 !important;
    
    background-color: rgba(46, 125, 50, 0.1);
  }

  .react-calendar__tile.approved {
    color: green !important;
    font-weight: bold;
    background-color: rgba(0, 128, 0, 0.1);
  }



  .react-calendar__tile.selected-date {
    background-color: #d0f0fd;
    border-radius: 30% !important;
  }



  .react-calendar__tile--now {
    border-radius: 30% !important;
    color: white !important;
  }
.react-calendar__tile--active {
  background-color: transparent !important;
  font-weight: normal !important;
  color: black !important;
}
  .react-calendar__tile--now.react-calendar__tile--active{
    background: #ffc108 !important;
    color: inherit !important;  
    font-weight: normal !important;
  }
  .react-calendar__tile--now.react-calendar__tile--active.present{
    color: #2e7d32 !important;
    font-weight: 900 !important;
  }
  .react-calendar__tile.selected-date {
    background-color: #90ee90 !important;
    color: black !important;
  }
  .react-calendar__tile--now.selected-date.leave {
    background-color: #f7cda3 !important;
    color: black !important; 
  }

  .react-calendar__tile:disabled {
    background-color: inherit;
    color: #ababab;
  }

  /* Add styles for disabled state */
  .react-calendar__tile.disabled-tile {
    pointer-events: none;
    opacity: 0.6;
  }
  
  .calendar-disabled .react-calendar__navigation button {
    pointer-events: none;
    opacity: 0.5;
  }
  
  .calendar-disabled .react-calendar__tile {
    pointer-events: none;
    opacity: 0.6;
  }

  /* Weekday headers */
  .react-calendar__month-view__weekdays {
    margin: 0 8px 0 0;
    color: #27568c;
  }

  .react-calendar__month-view__weekdays__weekday {
    background: #f2f2f2;
    margin-inline: 2px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: bold;
    margin-bottom: 2px;
    color: #27568c !important;
  }
.present{
font-weight: 900 !important
}
  .react-calendar__month-view__weekdays__weekday abbr {
    text-decoration: none;
    border-bottom: none;
  }

  /* Remove red from weekends */
  .react-calendar__month-view__days__day--weekend {
    color: inherit;
  }

  /* Year/Decade/Century view tile padding */
  .react-calendar__year-view .react-calendar__tile,
  .react-calendar__decade-view .react-calendar__tile,
  .react-calendar__century-view .react-calendar__tile {
    padding: 1.2em 0.8em;
  }
`;
  const [submitted, setSubmit] = useState(false);

  const tileContent = ({ date, view }) => {
    const dateStr = moment(date).format("YYYY-MM-DD");
    if (view === "month" && calendarData.approved.includes(dateStr)) {
      return <span style={{ color: 'green', fontWeight: 'bold', marginLeft: 2, fontSize: "9px" }}>p</span>;
    }

    return null;
  };

  return (
    <div className="attendance-container">
      {/* Show loading spinner or disabled overlay */}
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: '8px'
        }}>
          <FaSpinner className="fa-spin" style={{ fontSize: '24px', color: '#007deb' }} />
        </div>
      )}
      
      <StyledCalendar
        key={activeStartDate.toString()}
        value={value}
        onChange={handleDayClick}
        onActiveStartDateChange={handleActiveStartDateChange}
        tileClassName={tileClassName}
        tileDisabled={tileDisabled}
        prev2Label={null}
        next2Label={null}
        tileContent={tileContent}
        minDate={EMPDOJ ? new Date(EMPDOJ) : null}
        activeStartDate={activeStartDate}
        className={`custom-calendar ${isCalendarDisabled ? 'calendar-disabled' : ''}`}
      />

      <div className="col-md-12">
        <div className="d-flex justify-content-between">
          <button
            onClick={() => {
              setAttendanceCalenderOpen(false);
              setSelectedDates([new Date().toDateString()]);
            }}
            style={{
              backgroundColor: "#007deb",
              cursor: "pointer",
              padding: "3px 8px",
              borderRadius: "3px",
              color: "#F3F8FF",
              textDecoration: "none",
              border: "none",
              display: "inline-block",
              fontSize: "12px",
              fontWeight: "normal",
              transition: "all 0.3s ease",
            }}
          >
            Close
          </button>
          
          {(!(userShiftID === 1 || userShiftID === 2) || canMarkAttendanceSpecial) && (
            <button
              onClick={handleSubmit}
              disabled={isLoading || isCalendarDisabled}
              style={{
                backgroundColor: (isLoading || isCalendarDisabled) ? "#cccccc" : "#007deb",
                cursor: (isLoading || isCalendarDisabled) ? "not-allowed" : "pointer",
                padding: "3px 8px",
                borderRadius: "3px",
                color: "#F3F8FF",
                textDecoration: "none",
                border: "none",
                display: "inline-block",
                fontSize: "12px",
                fontWeight: "normal",
                transition: "all 0.3s ease",
                opacity: (isLoading || isCalendarDisabled) ? 0.6 : 1,
              }}
            >
              {submitted ? (
                <FaSpinner className="sm-spinner" />
              ) : (
                "Submit"
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttendanceCalendar;